import React, { forwardRef, useId } from 'react';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = false,
      className = '',
      disabled,
      id,
      'aria-describedby': describedBy,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const controlId = id || generatedId;
    const feedbackId = `${controlId}-feedback`;
    const description = [describedBy, (error || helperText) ? feedbackId : undefined].filter(Boolean).join(' ') || undefined;
    const baseStyles = 'field-control resize-y min-h-[100px]';
    const errorStyles = error ? 'border-status-danger focus:ring-status-danger/20 focus:border-status-danger' : 'border-border-default';

    return (
      <div className={`min-w-0 ${fullWidth ? 'w-full' : ''}`}>
        {label && (
          <label htmlFor={controlId} className="field-label">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={controlId}
          aria-invalid={error ? true : undefined}
          aria-describedby={description}
          className={`${baseStyles} ${errorStyles} ${className}`}
          disabled={disabled}
          {...props}
        />
        {error && (
          <p id={feedbackId} className="mt-1.5 text-xs text-status-danger">{error}</p>
        )}
        {helperText && !error && (
          <p id={feedbackId} className="mt-1.5 text-xs text-text-muted">{helperText}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
